import { appointmentFormCustomFieldSchema, publicBookingSnapshotSchema, publicAppointmentFormSchema, type PublicAppointmentForm, type PublicBookingSnapshot } from '@easyflow/shared';
import { z } from 'zod';
import type {
  AppointmentFormConfig,
  ServiceItem,
  ConsultantItem,
  SlotItem,
  CustomerData,
  BookingResult,
  BrandingConfig,
  ProductItem,
} from './types';

const DEFAULT_BASE = '/api/calendar/public';

const displayCustomFieldSchema = appointmentFormCustomFieldSchema.extend({
  type: z.string(),
  required: z.boolean().default(false),
});

/** Opaque legacy JSON stays on the wire; render only safely narrowed fields. */
export function mapCustomFields(fields: readonly unknown[]) {
  return fields.flatMap(value => {
    const parsed = displayCustomFieldSchema.safeParse(value);
    return parsed.success ? [parsed.data] : [];
  });
}

export interface ApiOptions {
  baseUrl?: string;
  /** Captcha token from either hCaptcha or Google reCAPTCHA. */
  captchaToken?: string;
  /** @deprecated Use `captchaToken` — kept for backward compatibility. */
  hcaptchaToken?: string;
  /** Optional public API key (`pk_live_…`) — sent as `X-API-Key` on every call. */
  publicKey?: string;
  /**
   * Task #2846 — JWT dell'editor admin, usato SOLO dall'anteprima live per
   * esonerare le chiamate di lettura dal rate limit pubblico (30/min). Inviato
   * come `Authorization: Bearer`. Mai impostato dal widget pubblico incorporato.
   */
  authToken?: string;
}

// ===== Backend wire types (snake_case) =====
type WireBranding = NonNullable<PublicAppointmentForm['branding']>;
interface WireService {
  id: string;
  name: string;
  description?: string | null;
  duration: number;
  color?: string | null;
  required_skills?: Array<{ id: string; name: string; color?: string }>;
  // Task #515: prodotti collegati alla tipologia (per lo step "Servizio").
  products?: Array<{ id: string; name: string; description?: string | null; price?: number | null }>;
}
interface WireConsultant {
  id: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  photo_url?: string | null;
  bio?: string | null;
  skills?: Array<{ id: string; name: string; level?: string }>;
}
interface WireSlot {
  start: string;
  end: string;
  consultant_id?: string;
}
interface WireBookingResult {
  appointmentId: string;
  cancelToken: string;
  status: 'confirmed' | 'pending';
}
export interface PublicBookingResponse {
  id: string;
  title: string;
  startTime: string;
  endTime: string;
  status: string;
  typeName: string | null;
  customerName: string | null;
  customerEmail: string | null;
  formName: string;
  branding: BrandingConfig | null;
  cancelDeadlineHours: number;
  rescheduleDeadlineHours: number;
}

// ===== HTTP =====
async function request<T>(path: string, init?: RequestInit & { baseUrl?: string; publicKey?: string; authToken?: string }): Promise<T> {
  const baseUrl = init?.baseUrl || DEFAULT_BASE;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((init?.headers as Record<string, string>) || {}),
  };
  if (init?.publicKey) headers['X-API-Key'] = init.publicKey;
  // Task #2846 — solo l'anteprima live passa authToken (esonero rate limit).
  if (init?.authToken) headers['Authorization'] = `Bearer ${init.authToken}`;
  const res = await fetch(`${baseUrl}${path}`, { ...init, headers });
  const json = await res.json().catch(() => ({} as Record<string, unknown>));
  if (!res.ok) {
    const j = json as { error?: { message?: string; code?: string } | string; message?: string };
    const errMsg = typeof j.error === 'string' ? j.error : (j.error?.message || j.message || `HTTP ${res.status}`);
    const err = new Error(errMsg) as Error & { status?: number; code?: string };
    err.status = res.status;
    if (typeof j.error === 'object' && j.error?.code) err.code = j.error.code;
    // Tag captcha errors so the UI can show a dedicated message + retry.
    if (!err.code) {
      const lower = errMsg.toLowerCase();
      if (res.status === 403 && lower.includes('captcha')) err.code = 'CAPTCHA_FAILED';
      else if (res.status === 400 && lower.includes('captcha')) err.code = 'CAPTCHA_MISSING';
    }
    throw err;
  }
  const j = json as { data?: T; success?: boolean };
  return (j.data ?? (json as T));
}

// ===== Mappers (snake_case wire → camelCase widget) =====
function mapBranding(b: WireBranding | null | undefined): BrandingConfig {
  const src = b || {};
  return {
    primaryColor: src.primaryColor || src.primary_color || '#6366f1',
    accentColor: src.accentColor || src.accent_color || '#ec4899',
    logoUrl: src.logoUrl || src.logo_url || undefined,
    fontFamily: src.fontFamily || src.font_family || undefined,
    defaultTheme: (src.defaultTheme || src.default_theme || 'light'),
  };
}

export function mapForm(w: PublicAppointmentForm): AppointmentFormConfig {
  return {
    id: w.id,
    slug: w.slug,
    name: w.name,
    description: w.description || undefined,
    isActive: true,
    style: w.widget_style || 'calcom',
    branding: mapBranding(w.branding),
    serviceIds: [],
    consultantIds: [],
    // Task #2786/#2846 — modalità consulente unica (3 stati). Il legacy 'skill' è
    // normalizzato a 'skill_shifts'. Fallback robusto ai flag legacy se un nodo
    // backend non aggiornato non espone ancora il campo (rolling deploy):
    // choice=false → 'any'; altrimenti 'skill_shifts'.
    consultantFilterMode:
      w.consultant_filter_mode === 'any' || w.consultant_filter_mode === 'manual'
        ? w.consultant_filter_mode
        : w.consultant_filter_mode === 'skill_shifts'
          ? 'skill_shifts'
          : (w.allow_consultant_choice === false ? 'any' : 'skill_shifts'),
    consultantSkillFilter: w.consultant_skill_filter === true,
    allowStoreChoice: !!w.allow_store_choice,
    stores: Array.isArray(w.stores)
      ? w.stores.map((s) => ({ id: s.id, name: s.name, city: s.city || undefined }))
      : [],
    defaultStore: w.default_store
      ? { id: w.default_store.id, name: w.default_store.name, city: w.default_store.city || undefined }
      : null,
    rules: {
      autoConfirm: !!w.auto_confirm,
      minLeadTimeHours: (w.min_advance_booking ?? 0) / 60,
      cancelWindowHours: w.cancel_deadline_hours ?? 24,
      maxAdvanceDays: w.max_advance_booking ?? 90,
    },
    customFields: mapCustomFields(w.custom_fields ?? []),
    privacy: {
      privacyText: w.privacy_text || 'Accetto l\'informativa sulla privacy *',
      marketingOptIn: !!w.marketing_opt_in,
      successMessage: w.confirmation_message || 'Prenotazione confermata!',
      pendingMessage: w.confirmation_message || 'Richiesta inviata, in attesa di approvazione',
      redirectUrl: w.redirect_url || undefined,
    },
    notifications: {
      emailCustomer: w.send_customer_email ?? true,
      emailConsultant: w.send_consultant_email ?? true,
      attachIcs: w.attach_ics ?? true,
    },
    antiAbuse: {
      hcaptchaEnabled: w.captcha_provider === 'hcaptcha',
      hcaptchaSiteKey: w.captcha_provider === 'hcaptcha' ? (w.captcha_site_key || undefined) : undefined,
      recaptchaEnabled: w.captcha_provider === 'recaptcha',
      recaptchaSiteKey: w.captcha_provider === 'recaptcha' ? (w.captcha_site_key || undefined) : undefined,
    },
    requireCustomerName: !!w.require_customer_name,
    requireCustomerEmail: !!w.require_customer_email,
    requireCustomerPhone: !!w.require_customer_phone,
    honeypotFieldName: w.honeypot_field_name || 'website_url',
    captchaProvider: w.captcha_provider || undefined,
    serviceSelectionMode: w.service_selection_mode || 'auto',
    showPrice: w.show_price === true,
    gdpr: {
      document: w.gdpr_document || null,
      consents: Array.isArray(w.gdpr_consents) ? w.gdpr_consents : [],
    },
  };
}

function mapProduct(p: { id: string; name: string; description?: string | null; price?: number | null }): ProductItem {
  return {
    id: p.id,
    name: p.name,
    description: p.description || undefined,
    price: typeof p.price === 'number' ? p.price : null,
  };
}

function mapService(s: WireService): ServiceItem {
  return {
    id: s.id,
    name: s.name,
    description: s.description || undefined,
    durationMinutes: s.duration || 30,
    color: s.color || '#6366f1',
    skills: s.required_skills?.map((sk) => sk.name),
    products: (s.products || []).map(mapProduct),
  };
}

function mapConsultant(c: WireConsultant): ConsultantItem {
  const fullName = c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim();
  return {
    id: c.id,
    firstName: c.first_name || fullName.split(' ')[0] || '',
    lastName: c.last_name || fullName.split(' ').slice(1).join(' ') || '',
    fullName,
    sitePhotoUrl: c.photo_url || undefined,
    bio: c.bio || undefined,
    skills: c.skills?.map((s) => ({ name: s.name, level: typeof s.level === 'string' ? Number(s.level) || undefined : s.level })),
  };
}

function mapSlot(s: WireSlot): SlotItem {
  return { start: s.start, end: s.end, available: true, consultantId: s.consultant_id };
}

function mapBookingResponse(w: PublicBookingSnapshot): PublicBookingResponse {
  return {
    id: w.id,
    title: w.title,
    startTime: w.start_time,
    endTime: w.end_time,
    status: w.status,
    typeName: w.type_name,
    customerName: w.customer_name,
    customerEmail: w.customer_email,
    formName: w.form_name ?? 'Prenotazione',
    branding: w.branding ? mapBranding(w.branding) : null,
    cancelDeadlineHours: w.cancel_deadline_hours ?? 24,
    rescheduleDeadlineHours: w.reschedule_deadline_hours ?? 24,
  };
}

// Task #2841 — Override di bozza per l'anteprima live "viva". L'editor passa la
// config NON salvata via query `pv*`; il backend li applica in-memory SOLO per
// richieste editor autenticate (JWT admin). Il widget pubblico non li invia mai.
export interface EditorPreviewParams {
  mode: 'any' | 'skill_shifts' | 'manual';
  skillFilter?: boolean;
  /** Roster whitelist di bozza (solo 'manual'). */
  roster?: string[];
  storeId?: string;
}

function appendPreviewParams(qs: URLSearchParams, p?: EditorPreviewParams): void {
  if (!p) return;
  qs.set('pvMode', p.mode);
  if (p.skillFilter !== undefined) qs.set('pvSkillFilter', p.skillFilter ? '1' : '0');
  if (p.storeId !== undefined) qs.set('pvStoreId', p.storeId);
  if (p.roster !== undefined) qs.set('pvRoster', p.roster.join(','));
}

// ===== Public API =====
export const appointmentPublicApi = {
  getForm: async (slug: string, opts?: ApiOptions): Promise<AppointmentFormConfig> => {
    const w = await request<unknown>(`/forms/${encodeURIComponent(slug)}`, { baseUrl: opts?.baseUrl, publicKey: opts?.publicKey, authToken: opts?.authToken });
    return mapForm(publicAppointmentFormSchema.parse(w));
  },
  getServices: async (slug: string, opts?: ApiOptions): Promise<ServiceItem[]> => {
    const w = await request<WireService[]>(`/forms/${encodeURIComponent(slug)}/services`, { baseUrl: opts?.baseUrl, publicKey: opts?.publicKey, authToken: opts?.authToken });
    return (w || []).map(mapService);
  },
  getConsultants: async (slug: string, serviceId: string, opts?: ApiOptions & { productId?: string; storeId?: string; date?: string; preview?: EditorPreviewParams }): Promise<ConsultantItem[]> => {
    const qs = new URLSearchParams();
    // Task #2786 — serviceId opzionale: senza servizio il backend restituisce
    // l'intero pool di consulenti idonei (filtro skill applicato solo se passato).
    if (serviceId) qs.set('serviceId', serviceId);
    if (opts?.productId) qs.set('productId', opts.productId);
    if (opts?.storeId) qs.set('storeId', opts.storeId);
    // Task #2846 — data scelta: in 'skill_shifts' restringe la rosa a chi è in turno.
    if (opts?.date) qs.set('date', opts.date);
    appendPreviewParams(qs, opts?.preview);
    const w = await request<WireConsultant[]>(
      `/forms/${encodeURIComponent(slug)}/consultants?${qs.toString()}`,
      { baseUrl: opts?.baseUrl, publicKey: opts?.publicKey, authToken: opts?.authToken },
    );
    return (w || []).map(mapConsultant);
  },
  getSlots: async (
    slug: string,
    params: { serviceId: string; consultantId: string; date: string; productId?: string; storeId?: string; preview?: EditorPreviewParams },
    opts?: ApiOptions & { signal?: AbortSignal },
  ): Promise<SlotItem[]> => {
    const qsObj: Record<string, string> = {
      serviceId: params.serviceId,
      consultantId: params.consultantId,
      date: params.date,
    };
    if (params.productId) qsObj.productId = params.productId;
    if (params.storeId) qsObj.storeId = params.storeId;
    const qs = new URLSearchParams(qsObj);
    appendPreviewParams(qs, params.preview);
    const q = qs.toString();
    const w = await request<WireSlot[]>(`/forms/${encodeURIComponent(slug)}/slots?${q}`, { baseUrl: opts?.baseUrl, publicKey: opts?.publicKey, authToken: opts?.authToken, signal: opts?.signal });
    return (w || []).map(mapSlot);
  },
  book: async (
    slug: string,
    payload: {
      serviceId: string;
      /** Task #515: id del prodotto/servizio scelto (opzionale). */
      productId?: string;
      /** Task #2752: sede scelta dal cliente (solo se allowStoreChoice). */
      storeId?: string;
      consultantId: string;
      slot: { start: string; end: string };
      customer: CustomerData;
      utm?: Record<string, string>;
      honeypotFieldName?: string;
      honeypotValue?: string;
      /** Task #1211: id+versione del documento privacy mostrato all'utente. */
      privacyDocumentId?: string;
      privacyDocumentVersion?: number;
    },
    opts?: ApiOptions,
  ): Promise<BookingResult> => {
    const honeypotFieldName = payload.honeypotFieldName || 'website_url';
    const body: Record<string, unknown> = {
      serviceId: payload.serviceId,
      ...(payload.productId ? { productId: payload.productId } : {}),
      ...(payload.storeId ? { storeId: payload.storeId } : {}),
      consultantId: payload.consultantId,
      start: payload.slot.start,
      customerName: payload.customer.name,
      customerEmail: payload.customer.email,
      customerPhone: payload.customer.phone,
      privacyAccepted: payload.customer.privacyAccepted,
      ...(payload.privacyDocumentId ? { privacyDocumentId: payload.privacyDocumentId } : {}),
      ...(typeof payload.privacyDocumentVersion === 'number'
        ? { privacyDocumentVersion: payload.privacyDocumentVersion }
        : {}),
      marketingAccepted: !!payload.customer.marketingAccepted,
      ...(payload.customer.gdprConsents && Object.keys(payload.customer.gdprConsents).length > 0
        ? { gdprConsents: payload.customer.gdprConsents }
        : {}),
      customFields: payload.customer.customAnswers || {},
      utm: payload.utm || {},
      [honeypotFieldName]: payload.honeypotValue || '',
    };
    const captchaToken = opts?.captchaToken || opts?.hcaptchaToken;
    if (captchaToken) body.captchaToken = captchaToken;
    const r = await request<WireBookingResult>(`/forms/${encodeURIComponent(slug)}/book`, {
      baseUrl: opts?.baseUrl,
      publicKey: opts?.publicKey,
      method: 'POST',
      body: JSON.stringify(body),
    });
    return { appointmentId: r.appointmentId, cancelToken: r.cancelToken, status: r.status };
  },
  getBooking: async (cancelToken: string, opts?: ApiOptions): Promise<PublicBookingResponse> => {
    const w = await request<unknown>(
      `/appointments/public/${encodeURIComponent(cancelToken)}`,
      { baseUrl: opts?.baseUrl, publicKey: opts?.publicKey },
    );
    return mapBookingResponse(publicBookingSnapshotSchema.parse(w));
  },
  cancelBooking: async (cancelToken: string, opts?: ApiOptions): Promise<{ success: boolean }> => {
    return request<{ success: boolean }>(
      `/appointments/public/${encodeURIComponent(cancelToken)}/cancel`,
      { baseUrl: opts?.baseUrl, publicKey: opts?.publicKey, method: 'POST', body: JSON.stringify({}) },
    );
  },
  rescheduleBooking: async (
    cancelToken: string,
    start: string,
    opts?: ApiOptions,
  ): Promise<{ appointmentId: string }> => {
    return request<{ appointmentId: string }>(
      `/appointments/public/${encodeURIComponent(cancelToken)}/reschedule`,
      { baseUrl: opts?.baseUrl, publicKey: opts?.publicKey, method: 'POST', body: JSON.stringify({ start }) },
    );
  },
};
